/**
 * Dot and arrow markers for the Longitude Study.
 *
 * The study returns event DATES. A vertical line needs nothing else, but a marker has to sit
 * against the bar, so each date is resolved to its bar and to that bar's high or low.
 *
 * ---------------------------------------------------------------------------------------------
 * Which way an arrow points.
 * ---------------------------------------------------------------------------------------------
 * The study itself reports no direction — it says when two bodies reached an angular separation,
 * which is neither bullish nor bearish. The direction therefore comes from PRICE CONTEXT at the
 * event bar, which is a statement about the bar rather than about the aspect, and is labelled as
 * such:
 *
 *   Down arrow ABOVE the bar   the bar is a local high, or price rose into it
 *   Up arrow BELOW the bar     the bar is a local low, or price fell into it
 *
 * A pivot wins over momentum when both apply: a confirmed local extreme is a stronger statement
 * than the direction of the approach, and at a top those two disagree by construction — price
 * rises INTO a high, so momentum alone would mark every peak the same as every rally.
 */

/** OHLC column layout — ECharts candlestick order, so low is 2 and high is 3. */
const OHLC_CLOSE = 1;
const OHLC_LOW = 2;
const OHLC_HIGH = 3;

/** Fraction of the bar's own range left between the marker and the price it marks. */
const CLEARANCE = 0.02;


/** Bars either side used to call a local extreme at the event bar. */
export const PIVOT_LOOKAROUND = 2;

/** Is this bar the highest / lowest of its immediate neighbourhood? */
export function localExtreme(ohlc, i, n = PIVOT_LOOKAROUND) {
  if (!Array.isArray(ohlc) || !ohlc[i]) return null;
  const at = (k, col) => (ohlc[k] ? ohlc[k][col] : null);
  const high = at(i, OHLC_HIGH);
  const low = at(i, OHLC_LOW);

  let isHigh = typeof high === 'number';
  let isLow = typeof low === 'number';
  for (let k = 1; k <= n; k++) {
    [i - k, i + k].forEach((j) => {
      const h = at(j, OHLC_HIGH);
      const l = at(j, OHLC_LOW);
      // Bars beyond the ends are simply absent; an event on the first or last bar can still be
      // called from the neighbours it does have rather than being disqualified.
      if (typeof h === 'number' && h >= high) isHigh = false;
      if (typeof l === 'number' && l <= low) isLow = false;
    });
  }
  if (isHigh) return 'high';
  if (isLow) return 'low';
  return null;
}

/**
 * 'down' for an arrow above the bar, 'up' for one below it.
 *
 * A local extreme decides it outright. Failing that the approach does: price rising into the bar
 * gets a down arrow above, price falling into it an up arrow below. With neither — no previous
 * bar to compare against — it defaults to above, which matches how the dots are placed.
 */
export function markerDirection(ohlc, i) {
  const extreme = localExtreme(ohlc, i);
  if (extreme === 'high') return 'down';
  if (extreme === 'low') return 'up';

  const prev = ohlc[i - 1];
  const cur = ohlc[i];
  if (prev && cur) {
    const prevClose = prev[OHLC_CLOSE];
    const curClose = cur[OHLC_CLOSE];
    if (typeof prevClose === 'number' && typeof curClose === 'number' && prevClose !== curClose) {
      return curClose > prevClose ? 'down' : 'up';
    }
  }
  return 'down';
}

const ymd = (d) => (typeof d === 'string' ? d.slice(0, 10) : '');

/**
 * Builds one scatter overlay carrying every event.
 *
 * One overlay rather than one per date: a study can return hundreds of events, and a series each
 * would bloat the chart option and make the legend useless.
 *
 * @param {Array<string>} dates    event dates from the study
 * @param {object} barData         { dates, ohlc }
 * @param {object} opts            { displayType: 'dot'|'arrow', color, label }
 * @returns {object|null} a pivotMarkers overlay, or null when nothing could be placed
 */
export function buildLongitudeMarkers(dates, barData, { displayType = 'dot', color = '#1976d2', label = 'Longitude study' } = {}) {
  if (!Array.isArray(dates) || dates.length === 0) return null;
  if (!barData || !Array.isArray(barData.dates) || !Array.isArray(barData.ohlc)) return null;

  // One pass to index the chart, rather than indexOf per event: a study with hundreds of dates
  // against a chart with thousands of bars is otherwise quadratic for no reason.
  const indexByDate = new Map();
  barData.dates.forEach((d, i) => {
    const key = ymd(d);
    if (key && !indexByDate.has(key)) indexByDate.set(key, i);
  });

  const points = [];
  dates.forEach((raw) => {
    const key = ymd(raw);
    const idx = indexByDate.get(key);
    // An event outside the loaded range has no bar to attach to. Skipped rather than clamped to
    // the nearest one, which would place a marker on a date the event did not occur.
    if (idx === undefined) return;

    const bar = barData.ohlc[idx];
    if (!bar) return;
    const high = bar[OHLC_HIGH];
    const low = bar[OHLC_LOW];
    if (typeof high !== 'number' || !Number.isFinite(high)) return;
    if (typeof low !== 'number' || !Number.isFinite(low)) return;

    const dir = markerDirection(barData.ohlc, idx);
    const above = dir === 'down';

    // Placed clear of the bar rather than on it, so the marker never hides the extreme it marks.
    const range = Math.max(high - low, Math.abs(high) * 0.001);
    points.push({
      date: barData.dates[idx],
      price: above ? high + range * CLEARANCE : low - range * CLEARANCE,
      position: above ? 'above' : 'below',
      direction: dir,
      // ECharts' 'arrow' symbol points UP, so a down arrow is the same glyph rotated. Set per
      // point because direction varies within this one overlay.
      symbolRotate: dir === 'down' ? 180 : 0,
      color,
      label: '',
    });
  });

  if (points.length === 0) return null;

  return {
    type: 'pivotMarkers',
    label,
    // Rotation is per point, since direction varies within this overlay.
    symbol: displayType === 'arrow' ? 'arrow' : 'circle',
    symbolSize: displayType === 'arrow' ? 11 : 8,
    color,
    points,
    displayType,
    id: `longitude-${displayType}-${points.length}-${points[0].date}`,
  };
}

export default buildLongitudeMarkers;
